import * as React from 'react';
import {
  ACTION_WIDGET_SEE_MORE,
  CATEGORY_WIDGET_CAROUSEL,
  CATEGORY_WIDGET_GRID,
  CATEGORY_WINERY_PAGE,
} from 'utils/analytics/eventTypesConstants';
import { CardSectionTitle } from './CardSectionTitle';
import { IconArrowRight } from '@tock/icons';
import { IconArrowRight as IconArrowRightLegacy } from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Link } from 'omakase';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      padding: theme.spacing(1.5),
      marginLeft: theme.spacing(-1.5),
      marginRight: theme.spacing(-1.5),
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'column',
    },
    rowFlexRow: {
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
      },
    },
    gridContent: {
      paddingTop: '0 !important',
    },
    showMoreContent: {
      flex: 1,
      paddingTop: theme.spacing(6),
    },
    mobileOnly: {
      [theme.breakpoints.up('sm')]: {
        display: 'none !important', // needed to override Link's styles
      },
    },
    nonMobileOnly: {
      display: 'none !important', // needed to override Link's styles
      [theme.breakpoints.up('sm')]: {
        display: 'inline-flex !important', // needed to override Link's styles
      },
    },
    arrowLinkContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      flexDirection: 'row',
    },
    arrowLinkSubSection: {
      marginTop: theme.spacing(0.5),
    },
    subSectionTitleCell: {
      paddingBottom: `${theme.typography.pxToRem(12)} !important`,
      [theme.breakpoints.up('sm')]: {
        paddingBottom: `${theme.typography.pxToRem(12)} !important`,
      },
    },
  })
);

type Containing =
  | typeof CATEGORY_WIDGET_CAROUSEL
  | typeof CATEGORY_WIDGET_GRID
  | typeof CATEGORY_WINERY_PAGE;

// isTitle means it's the first widget on the page, and the content widgets are fully driving the page.
interface WidgetContainerProps {
  title: string;
  subtitle?: string;
  isTitle: boolean;
  isSubSection?: boolean;
  href?: string;
  loadMore?: React.ReactNode;
  gridContentClassName?: string;
  contains: Containing;
  className?: string;
}

export const WidgetContainer: React.FunctionComponent<WidgetContainerProps> = ({
  children,
  gridContentClassName,
  href,
  isSubSection,
  isTitle,
  loadMore,
  subtitle,
  title,
  contains,
  className,
}) => {
  const classes = useStyles();
  const onClickSeeMoreGA = () => {
    window.analytics.sendEvent(contains, ACTION_WIDGET_SEE_MORE, title);
  };
  const arrowLink = (className: string, dataTestId: string) => (
    <Link
      className={className}
      size="large"
      aria-label={`Explore all ${title}`} /* If you update explore all copy, also update explore all copy below. */
      href={href}
      data-testid={dataTestId}
      onClick={onClickSeeMoreGA}
      iconEnd={
        <IconMigrationUtil legacyIcon={<IconArrowRightLegacy />} newIcon={<IconArrowRight />} />
      }
    >
      {/* If you update explore all copy here update explore all above. */}
      Explore all
    </Link>
  );
  const desktopArrowLink = arrowLink(
    clsx(classes.nonMobileOnly, isSubSection && classes.arrowLinkSubSection),
    `show-more-link_${title.replace(/\W/g, '')}_mobile`
  );
  const mobileArrowLink = arrowLink(
    clsx(classes.mobileOnly, isSubSection && classes.arrowLinkSubSection),
    `show-more-link_${title.replace(/\W/g, '')}_mobile`
  );

  return (
    <>
      {title ? (
        <div className={clsx(classes.row, { [classes.subSectionTitleCell]: isSubSection })}>
          <CardSectionTitle
            variant={isSubSection ? 'subsection' : 'default'}
            isTitle={isTitle}
            title={title}
            subtitle={subtitle}
            arrowLink={href ? desktopArrowLink : undefined}
          />
          {href && mobileArrowLink}
        </div>
      ) : null}
      <div
        className={clsx(
          classes.row,
          classes.rowFlexRow,
          classes.gridContent,
          className,
          gridContentClassName
        )}
        data-testid="experience-card"
      >
        {children}
      </div>
      {loadMore && (
        <div className={classes.row}>
          <div className={classes.showMoreContent}>{loadMore}</div>
        </div>
      )}
    </>
  );
};
